import React, { useState, useEffect, useRef, useId } from 'react'
import { useLanguage } from '../../i18n/index.jsx'
import { Input, Btn } from '../ui/index.jsx'
import api from '../../utils/api.js'
import { logError } from '../../utils/logError.js'
import { useOnboardingSlot } from '../cohesion/onboardingOrchestrator'
import type { User, Department } from '../../types/domain'

interface ProfileCompletionModalProps {
  user: User | null
  onComplete: () => void
  onUserUpdate?: (user: User) => void
}

export default function ProfileCompletionModal({ user, onComplete, onUserUpdate }: ProfileCompletionModalProps) {
  const { t } = useLanguage()
  const [form, setForm] = useState({
    work_title: user?.work_title || '',
    email: user?.email || '',
    phone: user?.phone || '',
    department_id: String(user?.department_id ?? ''),
  })
  const [departments, setDepartments] = useState<Department[]>([])
  const [saving, setSaving] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)
  const prevFocusRef = useRef<HTMLElement | null>(null)
  const titleId = useId()

  // UX-01 (2026-06 UX audit): never collide with the welcome/surface tours.
  // The modal queues behind any active tour and only renders once it holds
  // the single onboarding slot.
  const active = useOnboardingSlot('profile-completion', !user?.work_title, 1)

  useEffect(() => {
    api.get('/departments')
      .then(({ data }) => setDepartments(data as Department[]))
      .catch(err => logError('profileCompletion.loadDepartments', err))
  }, [])

  useEffect(() => {
    if (user?.work_title || !active) return
    prevFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    dialogRef.current?.focus()
    return () => {
      const el = prevFocusRef.current
      if (el && typeof el.focus === 'function' && document.body.contains(el)) {
        queueMicrotask(() => el.focus())
      }
    }
  }, [user?.work_title, active])

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setSaving(true)
    try {
      const { data } = await api.put('/users/me/profile', {
        ...form,
        department_id: form.department_id ? parseInt(form.department_id) : null,
      })
      if (onUserUpdate) onUserUpdate({ ...(user ?? {}), ...(data as Partial<User>) } as User)
      onComplete()
    } catch (err) { logError('profileCompletion.save', err) }
    finally { setSaving(false) }
  }

  const handleSkip = () => {
    localStorage.setItem('sw-profile-completion-skipped', '1')
    onComplete()
  }

  if (user?.work_title || !active) return null

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 'var(--z-fullscreen)',
      background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{
          background: 'var(--bg-surface)', borderRadius: 16, padding: 28, maxWidth: 420, width: '90%',
          border: '1px solid var(--border-default)', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', outline: 'none',
        }}>
        <h2 id={titleId} style={{ fontSize: 'var(--text-xl)', fontWeight: 700, margin: '0 0 4px' }}>{t('profileCompleteTitle')}</h2>
        <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: '0 0 20px' }}>{t('profileCompleteDesc')}</p>

        <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Input
            label={t('profileWorkTitle')}
            placeholder={t('profileWorkTitlePlaceholder')}
            value={form.work_title}
            onChange={e => setForm(f => ({ ...f, work_title: e.target.value }))}
          />
          <div>
            <label style={{ display: 'block', fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>
              {t('profileDepartment')}
            </label>
            <select
              value={form.department_id}
              onChange={e => setForm(f => ({ ...f, department_id: e.target.value }))}
              style={{
                width: '100%', padding: '8px 10px', borderRadius: 8, fontSize: 'var(--text-caption)',
                background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
                color: 'var(--text-primary)',
              }}
            >
              <option value="">{t('profileSelectDepartment')}</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <Input
            label={t('profileEmail')}
            type="email"
            placeholder="you@example.com"
            value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
          />
          <Input
            label={t('profilePhone')}
            type="tel"
            placeholder="+45 12 34 56 78"
            value={form.phone}
            onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
          />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <Btn variant="ghost" type="button" onClick={handleSkip}>{t('profileSkipForNow')}</Btn>
            <Btn variant="primary" type="submit" loading={saving}>{t('profileSaveAndContinue')}</Btn>
          </div>
        </form>
      </div>
    </div>
  )
}
